import React, { useState, useEffect } from 'react';
import { Modal, Table, Image, Typography, message, Button, Popconfirm, Space } from 'antd';
import { BookOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';
import { tagsApi, BookByTag } from '../../api/tags/tags.api';
import { Tag } from '../../common/tag.interface';
import { API_URL } from '../../enums/api.enums';
import './CModal.styles.scss';

const { Text } = Typography;

interface CModalViewTagBooksProps {
  visible: boolean;
  tag: Tag | null;
  onCancel: () => void;
}

// Sử dụng BookByTag interface từ API

const CModalViewTagBooks: React.FC<CModalViewTagBooksProps> = ({
  visible,
  tag,
  onCancel,
}) => {
  const [books, setBooks] = useState<BookByTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [imageRefreshTimestamp] = useState<number>(Date.now());

  useEffect(() => {
    if (visible && tag) {
      loadBooks();
    }
  }, [visible, tag]);

  const loadBooks = async () => {
    if (!tag) return;

    setLoading(true);
    try {
      console.log('Loading books for tag:', tag.id);
      const books: BookByTag[] = await tagsApi.getBooksByTag(tag.id);
      console.log('Books loaded:', books);
      setBooks(books);
    } catch (error) {
      message.error('Không thể tải danh sách sách');
      console.error('Error loading books by tag:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveBook = async (bookId: number) => {
    if (!tag) return;
    try {
      await tagsApi.removeBookFromTag(tag.id, bookId);
      setBooks(prev => prev.filter(b => b.id !== bookId));
      message.success('Đã xóa sách khỏi tag');
    } catch (error) {
      message.error('Không thể xóa sách khỏi tag');
      console.error('Error removing book from tag:', error);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price);
  };

  const columns = [
    {
      title: 'STT',
      width: 60,
      render: (_: any, __: any, index: number) => (
        <div style={{ textAlign: 'center' }}>
          {index + 1}
        </div>
      ),
    },
    {
      title: 'Ảnh bìa',
      dataIndex: 'thumbnailUrl',
      key: 'thumbnailUrl',
      width: 80,
      render: (url: string, record: BookByTag) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <img
            src={`${API_URL.HOST}/books/preview-images?id=${record.id}&t=${imageRefreshTimestamp}`}
            alt={record.name}
            width={50}
            height={60}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            onError={(e) => {
              // Fallback to a default image if preview-images fails
              const target = e.target as HTMLImageElement;
              target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA1MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yNSAyMEM5LjI2OCAyMCAxOCAyNS4yNjggMTggMzBTMjMuMjY4IDQwIDI1IDQwUzMyIDM0LjczMiAzMiAzMFMyNi43MzIgMjAgMjUgMjBaIiBmaWxsPSIjQ0NDIi8+CjxwYXRoIGQ9Ik0xNSA0NUgzNVYzNUgxNVY0NVoiIGZpbGw9IiNDQ0MiLz4KPC9zdmc+';
            }}
          />
        </div>
      ),
    },
    {
      title: 'Tên sách',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <div style={{ fontWeight: 500 }}>{text}</div>
      ),
    },
    {
      title: 'Tác giả',
      key: 'authors',
      render: (_: any, record: BookByTag) => (
        <div>
          {record.authorsBooks.map((authorBook, index) => (
            <span key={authorBook.author.id}>
              {authorBook.author.name}
              {index < record.authorsBooks.length - 1 && ', '}
            </span>
          ))}
        </div>
      ),
    },
    {
      title: 'Nhà xuất bản',
      key: 'publisher',
      render: (_: any, record: BookByTag) => (
        <div>{record.publishingHouse.name}</div>
      ),
    },
    {
      title: 'Giá sách',
      key: 'prices',
      render: (_: any, record: BookByTag) => (
        <div style={{ fontSize: '12px' }}>
          {record.pricePaper > 0 && (
            <div>Giấy: {formatPrice(record.pricePaper)}</div>
          )}
          {record.priceEbook > 0 && (
            <div>Ebook: {formatPrice(record.priceEbook)}</div>
          )}
          {record.priceAudio > 0 && (
            <div>Audio: {formatPrice(record.priceAudio)}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => (
        <div style={{ fontSize: '12px' }}>
          {moment(date).format('DD/MM/YYYY')}
        </div>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 80,
      render: (_: any, record: BookByTag) => (
        <Popconfirm
          title={`Xóa sách "${record.name}" khỏi tag "${tag?.name}"?`}
          onConfirm={() => handleRemoveBook(record.id)}
          okText="Xóa"
          cancelText="Hủy"
          okButtonProps={{ danger: true }}
        >
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            size="small"
          >
            Xóa
          </Button>
        </Popconfirm>
      ),
    },
  ];

  // Không cần pagination vì API trả về tất cả sách

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BookOutlined style={{ color: '#1890ff' }} />
          <span>Danh sách sách của tag: </span>
          <span style={{ 
            color: tag?.color || '#1890ff', 
            fontWeight: 600,
            backgroundColor: `${tag?.color || '#1890ff'}20`,
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: '14px'
          }}>
            {tag?.name}
          </span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={1200}
      footer={null}
      className="tags-modal"
    >
      <div style={{ marginBottom: 16 }}>
        <Text strong>Tổng số sách: </Text>
        <Text type="success" style={{ fontSize: 16, fontWeight: 600 }}>
          {books.length}
        </Text>
      </div>

      <Table
        columns={columns}
        dataSource={books}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
          showQuickJumper: true,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} của ${total} sách`,
        }}
        scroll={{ y: 400 }}
        size="small"
      />
    </Modal>
  );
};

export default CModalViewTagBooks;
